import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { canModifyContent } from '@/lib/account-roles';
import { usePerformersQuery } from '@/queries/performers';
import { useAuthStore } from '@/stores/auth-store';
import {
  HOME_TABS,
  useHomeDashboardStore,
} from '@/stores/home-dashboard-store';
import { Loading } from '../controls';

function InfoBlock({ icon, title, children }) {
  if (!children) {
    return null;
  }
  return (
    <section className="bethere-info-block">
      <h2 className="flex items-center gap-2 text-base font-bold text-[#121212]">
        <FontAwesomeIcon
          icon={icon}
          className="text-[var(--bethere-primary)]"
        />
        {title}
      </h2>
      {children}
    </section>
  );
}

function formatHref(url) {
  if (!url?.trim()) {
    return null;
  }
  const v = url.trim();
  return v.includes('://') ? v : `https://${v}`;
}

export function PerformerView(props) {
  const performerId = props.match?.params?.id;
  const currentUser = useAuthStore((s) => s.currentUser);
  const canModify = canModifyContent(currentUser);
  const setActiveTab = useHomeDashboardStore((s) => s.setActiveTab);

  const performersQ = usePerformersQuery();

  const performer = useMemo(() => {
    const list = performersQ.data ?? [];
    return (
      list.find((p) => String(p.performerId) === String(performerId)) ?? null
    );
  }, [performersQ.data, performerId]);

  if (performersQ.isPending) {
    return <Loading />;
  }

  if (performersQ.isError || !performer) {
    return (
      <div className="py-16 text-center text-red-600">
        Could not load this performer.{' '}
        <Link
          to="/"
          className="font-bold text-[var(--bethere-primary)]"
          onClick={() => setActiveTab(HOME_TABS.performers)}
        >
          Back to performers
        </Link>
      </div>
    );
  }

  const links = performer.links ?? {};
  const webHref = formatHref(links.web);
  const typeLine = [performer.type, performer.subtype]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className="bethere-festival-view">
      <nav className="breadcrumb-container" aria-label="Breadcrumb">
        <ul className="breadcrumb m-0 list-none p-0">
          <li>
            <Link to="/" onClick={() => setActiveTab(HOME_TABS.performers)}>
              My Performers
            </Link>
          </li>
          <li aria-current="page">{performer.performerName}</li>
        </ul>
        {canModify && (
          <Link
            to={`/performer/${performer.performerId}`}
            className="bethere-view-edit-link"
          >
            <FontAwesomeIcon icon={['fas', 'pen-to-square']} /> Edit performer
          </Link>
        )}
      </nav>

      <div className="detail-hero">
        {performer.photoURL ? (
          <img
            src={performer.photoURL}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-neutral-700 text-white/70">
            No image
          </div>
        )}
      </div>

      <div className="bethere-content-container">
        <div className="event-main-info min-w-0">
          <div className="glass-panel p-6 sm:p-8">
            <h1 className="mb-6 text-[length:var(--bethere-f-h1)] font-bold leading-[1.2] text-[#121212]">
              {performer.performerName}
            </h1>

            {performer.performerDescription && (
              <InfoBlock
                icon={['fas', 'circle-info']}
                title="General Information"
              >
                <p className="m-0 whitespace-pre-wrap leading-relaxed text-[var(--bethere-f-p)] text-[#121212]">
                  {performer.performerDescription}
                </p>
              </InfoBlock>
            )}

            {!performer.performerDescription && (
              <p className="m-0 text-[var(--muted-foreground)]">
                No description yet.
              </p>
            )}
          </div>
        </div>

        <aside className="bethere-detail-sidebar">
          <div className="glass-panel p-6">
            <ul className="meta-list m-0 list-none p-0">
              {typeLine && (
                <li>
                  <FontAwesomeIcon icon={['fas', 'tags']} />
                  <div>
                    <strong>Type</strong>
                    {typeLine}
                  </div>
                </li>
              )}
              {performer.hometown && (
                <li>
                  <FontAwesomeIcon icon={['fas', 'location-dot']} />
                  <div>
                    <strong>Hometown</strong>
                    {performer.hometown}
                  </div>
                </li>
              )}
              {(webHref ||
                links.facebook ||
                links.twitter ||
                links.instagram) && (
                <li className="event-socials">
                  <FontAwesomeIcon icon={['fas', 'link']} />
                  <div>
                    <strong>Website</strong>
                    {webHref && (
                      <a
                        href={webHref}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {links.web}
                      </a>
                    )}
                    <div className="event-socials-icons">
                      {links.facebook && (
                        <a
                          href={formatHref(links.facebook)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Facebook"
                        >
                          <FontAwesomeIcon icon={['fab', 'facebook']} />
                        </a>
                      )}
                      {links.twitter && (
                        <a
                          href={formatHref(links.twitter)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="X"
                        >
                          <FontAwesomeIcon icon={['fab', 'x-twitter']} />
                        </a>
                      )}
                      {links.instagram && (
                        <a
                          href={formatHref(links.instagram)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Instagram"
                        >
                          <FontAwesomeIcon icon={['fab', 'instagram']} />
                        </a>
                      )}
                    </div>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </aside>
      </div>
    </article>
  );
}
